import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Pricing from './pages/Pricing';
import PaymentSuccess from './pages/PaymentSuccess';
import Dashboard from './pages/Dashboard';
import TaxesScreen from './pages/TaxesScreen';
import CashflowScreen from './pages/CashflowScreen';
import ExpensesScreen from './pages/ExpensesScreen';
import YouScreen from './pages/YouScreen';
import Welcome from './pages/onboarding/Welcome';
import TaxSetup from './pages/onboarding/TaxSetup';
import IncomeCurve from './pages/onboarding/IncomeCurve';
import OnboardingExpenses from './pages/onboarding/OnboardingExpenses';
import OnboardingSummary from './pages/onboarding/Summary';
import AddExpense from './pages/expenses/AddExpense';
import EditExpense from './pages/expenses/EditExpense';
import IRSPayment from './pages/IRSPayment';
import Settings from './pages/Settings';
import TaxEdit from './pages/settings/TaxEdit';
import CurveEdit from './pages/settings/CurveEdit';
import LandingPage from './pages/LandingPage';
import AuthVerify from './pages/AuthVerify';

function AuthedRoute({ children }) {
  const { token } = useAuth();
  // Token presence only. Deliberately does NOT check ready, hasAccess, or
  // is_onboarded — Signup's login(null, jwt) path leaves user === null with a
  // valid token, and that user must still reach onboarding.
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function SubscribedRoute({ children }) {
  const { token, ready, hasAccess } = useAuth();
  // 1. No token → login (preserves original behavior; never to pricing).
  if (!token) return <Navigate to="/login" replace />;
  // 2. Token but /auth/me not resolved yet → wait. Do NOT bounce a paying
  //    user to /pricing during the loading window (user/hasAccess not known).
  if (!ready) return null;
  // 3. Token + ready + no access → the paywall.
  if (!hasAccess) return <Navigate to="/pricing" replace />;
  // 4. Token + ready + access (beta or active subscriber) → protected app.
  return children;
}

function PublicRoute({ children }) {
  const { token, ready, user } = useAuth();
  // Logged out: the landing page, regardless of anything else.
  if (!token) return children;
  // Unreachable today — AuthProvider renders a splash in place of its children
  // until ready, so nothing here can observe the pre-resolution state. Kept to
  // match SubscribedRoute and to stay correct if that gate is ever relaxed.
  if (!ready) return null;
  // Same rule Login.js:29 applies after a password login, so the site root and
  // a fresh login can't disagree about where an unfinished account belongs.
  // This is the re-entry path for bookmarks, autocomplete and PWA launches
  // (manifest start_url "." resolves here), where sending a paid-but-unonboarded
  // user to /dashboard left them on an empty shell.
  //
  // user is null with a live token only via Signup's login(null, jwt) fallback
  // (see AuthedRoute above) — a brand-new account, so falling through to
  // onboarding is the correct destination rather than a case to guard against.
  return user?.is_onboarded
    ? <Navigate to="/dashboard" replace />
    : <Navigate to="/onboarding/welcome" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/verify" element={<AuthVerify />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route
        path="/dashboard"
        element={
          <SubscribedRoute>
            <Dashboard />
          </SubscribedRoute>
        }
      />
      <Route
        path="/taxes"
        element={
          <SubscribedRoute>
            <TaxesScreen />
          </SubscribedRoute>
        }
      />
      <Route
        path="/cashflow"
        element={
          <SubscribedRoute>
            <CashflowScreen />
          </SubscribedRoute>
        }
      />
      <Route
        path="/expenses"
        element={
          <SubscribedRoute>
            <ExpensesScreen />
          </SubscribedRoute>
        }
      />
      <Route
        path="/you"
        element={
          <SubscribedRoute>
            <YouScreen />
          </SubscribedRoute>
        }
      />
      {/* Superseded by /expenses; kept so an existing bookmark still resolves. */}
      <Route path="/money" element={<Navigate to="/expenses" replace />} />
      <Route
        path="/onboarding/tax"
        element={
          <AuthedRoute>
            <TaxSetup />
          </AuthedRoute>
        }
      />
      <Route
        path="/onboarding/curve"
        element={
          <AuthedRoute>
            <IncomeCurve />
          </AuthedRoute>
        }
      />
      <Route
        path="/expenses/add"
        element={
          <SubscribedRoute>
            <AddExpense />
          </SubscribedRoute>
        }
      />
      <Route
        path="/expenses/edit"
        element={
          <SubscribedRoute>
            <EditExpense />
          </SubscribedRoute>
        }
      />
      <Route
        path="/irs-payment"
        element={
          <SubscribedRoute>
            <IRSPayment />
          </SubscribedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <SubscribedRoute>
            <Settings />
          </SubscribedRoute>
        }
      />
      <Route
        path="/settings/tax"
        element={
          <SubscribedRoute>
            <TaxEdit />
          </SubscribedRoute>
        }
      />
      <Route
        path="/settings/curve"
        element={
          <SubscribedRoute>
            <CurveEdit />
          </SubscribedRoute>
        }
      />
      <Route path="/onboarding" element={<Navigate to="/onboarding/welcome" replace />} />
      <Route
        path="/onboarding/summary"
        element={
          <AuthedRoute>
            <OnboardingSummary />
          </AuthedRoute>
        }
      />
      <Route
        path="/onboarding/expenses"
        element={
          <AuthedRoute>
            <OnboardingExpenses />
          </AuthedRoute>
        }
      />
      <Route
        path="/onboarding/welcome"
        element={
          <AuthedRoute>
            <Welcome />
          </AuthedRoute>
        }
      />
      <Route
        path="/"
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
